import type { TimerAdapter } from '../types';

/**
 * Real browser timer adapter
 * Abstracts timing operations for dependency injection and testing
 */
export class BrowserTimerAdapter implements TimerAdapter {
  setTimeout(callback: () => void, delayMs: number): number {
    return window.setTimeout(callback, delayMs);
  }

  clearTimeout(timeoutId: number): void {
    window.clearTimeout(timeoutId);
  }

  requestAnimationFrame(callback: () => void): number {
    return window.requestAnimationFrame(callback);
  }

  cancelAnimationFrame(frameId: number): void {
    window.cancelAnimationFrame(frameId);
  }

  delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
}

/**
 * Fake timer adapter for testing
 * Provides synchronous control over time for deterministic tests
 */
export class FakeTimerAdapter implements TimerAdapter {
  private timeouts: Map<number, { callback: () => void; triggerTime: number }> = new Map();
  private animationFrames: Map<number, () => void> = new Map();
  private nextId = 1;
  private currentTime = 0;

  setTimeout(callback: () => void, delayMs: number): number {
    const id = this.nextId++;
    this.timeouts.set(id, {
      callback,
      triggerTime: this.currentTime + delayMs,
    });
    return id;
  }

  clearTimeout(timeoutId: number): void {
    this.timeouts.delete(timeoutId);
  }

  requestAnimationFrame(callback: () => void): number {
    const id = this.nextId++;
    this.animationFrames.set(id, callback);
    return id;
  }

  cancelAnimationFrame(frameId: number): void {
    this.animationFrames.delete(frameId);
  }

  async delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.setTimeout(() => resolve(), ms);
    });
  }

  // Test helpers

  /**
   * Advance time by the specified amount and trigger any callbacks
   */
  async tick(ms: number): Promise<void> {
    this.currentTime += ms;

    // Trigger timeouts
    const triggered: number[] = [];
    for (const [id, timeout] of this.timeouts.entries()) {
      if (timeout.triggerTime <= this.currentTime) {
        timeout.callback();
        triggered.push(id);
      }
    }

    // Remove triggered timeouts
    for (const id of triggered) {
      this.timeouts.delete(id);
    }

    // Trigger animation frames
    const frames = Array.from(this.animationFrames.values());
    this.animationFrames.clear();
    for (const callback of frames) {
      callback();
    }

    // Allow promises to resolve
    await Promise.resolve();
  }

  /**
   * Run all pending timers until none remain
   */
  async runAll(): Promise<void> {
    while (this.timeouts.size > 0 || this.animationFrames.size > 0) {
      // Find next timeout
      let nextTime = Infinity;
      for (const timeout of this.timeouts.values()) {
        if (timeout.triggerTime < nextTime) {
          nextTime = timeout.triggerTime;
        }
      }

      if (nextTime !== Infinity) {
        await this.tick(nextTime - this.currentTime);
      } else {
        // Just run animation frames
        await this.tick(0);
      }
    }
  }

  /**
   * Get current time
   */
  now(): number {
    return this.currentTime;
  }

  /**
   * Reset timer state
   */
  reset(): void {
    this.timeouts.clear();
    this.animationFrames.clear();
    this.currentTime = 0;
    this.nextId = 1;
  }
}
